import { Col, Form, Row, Typography } from 'antd'
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { InputField } from 'src/components/form'
import {
  InputLabel,
  LabelRequire,
} from 'src/components/layout/Header/HeaderStyle'
import { UserRequestInterface } from 'src/interfaces/user'
import { UseFormReturn } from 'react-hook-form'
import LocalStorage from 'src/utils/LocalStorage'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import ButtonCustom from 'src/components/form/Button'
import { useRegister } from './useRegisterForm'
import addressRequest from 'src/api/requests/address'
import { PATH as AUTOMATIC_PLAN_PATH } from 'src/pages/automaticPlan'
import { getMenuById } from 'src/useCase/question'
import { getInfo, updateUserInfo } from 'src/useCase/user'
import { createUserTreatment } from 'src/useCase/userTreatment'
import { useAppDispatch, useAppSelector } from 'src/store'
import { getClinicDetail } from 'src/useCase/clinic'
import DuplicateAccountModal from 'src/components/Modal/DuplicateAccountModal'
import GenderSelection from 'src/components/form/GenderSelection'

type Props = {
  flowName?: string | null
  form: UseFormReturn<UserRequestInterface>
  asForm?: boolean
}

const UserInfoForm = ({ flowName, form, asForm = true }: Props) => {
  const { form: userForm, onSubmit: submitRegister } = useRegister()
  const navigate = useNavigate()
  const paramsUrl = useParams()
  const [searchParams] = useSearchParams()
  const menuId = searchParams.get('menuId') || paramsUrl?.id
  const clinicId = searchParams.get('clinicId')
  const planId = searchParams.get('planId')
  const dispatch = useAppDispatch()
  const userInfo = useAppSelector((state) => state.ui.app.authInfo)
  const [menuTargetGender, setMenuTargetGender] = useState<string>()
  const [userSelectedGender, setUserSelectedGender] = useState<string>()
  const [genderError, setGenderError] = useState(false)
  const [isAgreePolicy, setIsAgreePolicy] = useState(false)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
  const [isTelemedicine, setIsTelemedicine] = useState<boolean>(
    () => LocalStorage.getTelemedicine() === 'true',
  )
  const uuid = searchParams.get('uuid')
  const from = searchParams.get('from')
  const newForm = useMemo(() => {
    return isTelemedicine ? userForm : form
  }, [isTelemedicine, userForm, form])
  const genderFromUrl =
    searchParams.get('gender') || searchParams.get('gender_id')
  const isRequireGender =
    !userInfo?.gender_id && !genderFromUrl && from === 'LP' && !!planId

  const Component = asForm ? Form : 'div'

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    setFocus,
  } = newForm

  const handleBlur = async (value: string) => {
    if (!value) return

    if (newForm.formState.errors.email) return

    try {
      await addressRequest.verifyUser({ email: value })
      newForm.clearErrors('email')
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.errors?.email?.[0] || err?.response?.data?.message

      const isDuplicateError = apiMessage === 'このメールは既に使われています'

      if (isDuplicateError) {
        setIsDuplicateModalOpen(true)
      }

      newForm.setError('email', {
        type: 'manual',
        message: apiMessage,
      })
    }
  }

  const handleTelemedicineFlow = useCallback(async () => {
    if (!uuid) {
      return
    }

    try {
      const result = await dispatch(getClinicDetail({ uuid })).unwrap()
      const telemedicine = Boolean(result?.telemedicine_enabled)
      setIsTelemedicine(telemedicine)
    } catch {
      return
    }
  }, [uuid, dispatch])

  useEffect(() => {
    handleTelemedicineFlow()
  }, [handleTelemedicineFlow])

  useEffect(() => {
    if (menuId && clinicId) {
      dispatch(
        getMenuById({
          menuId,
          params: {
            clinic_id: clinicId,
            select_field: 'target_gender',
          },
        }),
      )
        .then((res: any) => {
          const gender = res.payload.target_gender
          setMenuTargetGender(`${gender}`)
        })
        .catch(() => '')
    }
  }, [menuId, clinicId])

  const handleFormError = () => {
    if (isRequireGender && !userSelectedGender) {
      setGenderError(true)
    }
    const err = newForm.formState.errors
    const firstErrName = Object.keys(err)[0] as keyof UserRequestInterface
    if (firstErrName) setFocus(firstErrName)
  }

  const handleFormSubmit = async (data: UserRequestInterface) => {
    const ANY_GENDER_ID = '2'

    // 1. Resolve effective gender from multiple potential sources
    const effectiveGender =
      userSelectedGender ||
      data.gender_id ||
      genderFromUrl ||
      (userInfo?.gender_id ? String(userInfo.gender_id) : '')

    // 2. Validation Phase (Fail-fast)
    if (isRequireGender && !userSelectedGender) {
      setGenderError(true)
      return
    }

    newForm.clearErrors('gender_id')

    const isGenderIncompatible = Boolean(
      menuTargetGender &&
        menuTargetGender !== ANY_GENDER_ID &&
        menuTargetGender !== effectiveGender,
    )

    if (isGenderIncompatible) {
      newForm.setError('gender_id', {
        type: 'manual',
        message: 'このメニューは選択した性別ではご利用できません。',
      })
      return
    }

    try {
      // 3. Synchronization Phase: Ensure the user account is up-to-date
      if (userInfo) {
        await dispatch(
          updateUserInfo({
            ...data,
            gender_id: effectiveGender,
          }),
        ).unwrap()
      } else {
        const registrationSuccess = await submitRegister(
          {
            ...data,
            gender_id: effectiveGender,
          },
          '',
          '',
        )
        if (!registrationSuccess) return
      }

      // 4. Initialization Phase: Create treatment and refresh state if menu is targeted
      if (menuId) {
        // We unwrap these to guarantee completion before navigating
        await dispatch(
          createUserTreatment({
            data: { menu_id: Number(menuId), plan_id: planId },
            form: newForm as any,
          }),
        ).unwrap()

        await dispatch(
          getInfo({
            with: 'userTreatments,userTreatments.reservations,userTreatments.reservations.answers',
          }),
        ).unwrap()
      }

      // 5. Progression Phase: Secure navigation to the questions flow
      const nextStepPath = AUTOMATIC_PLAN_PATH.QUESTIONS.replace(
        ':id',
        menuId || '',
      )
      navigate(`${nextStepPath}?${searchParams.toString()}`)
    } catch (error) {
      // All error feedback is centrally managed via thunk middleware.
      // We catch here to prevent unintended progression on operation failure.
    }
  }

  return (
    <React.Fragment>
      <StyleForm as={Component}>
        <Row className="justify-content-between">
          <Col xs={11}>
            <InputLabel className="font-weight-bold">姓</InputLabel>
            <InputField<UserRequestInterface>
              controller={{
                control,
                name: 'family_name',
              }}
              inputProps={{
                placeholder: '入力してください',
              }}
            />
          </Col>
          <Col xs={11} className="ml-10">
            <Row className="justify-content-between">
              <InputLabel className="font-weight-bold">名</InputLabel>
              <LabelRequire>
                <span>必須</span>
              </LabelRequire>
            </Row>
            <InputField<UserRequestInterface>
              controller={{
                control,
                name: 'given_name',
              }}
              inputProps={{
                placeholder: '入力してください',
              }}
            />
          </Col>
        </Row>
        <Row className="justify-content-between">
          <Col xs={11}>
            <InputLabel className="font-weight-bold">姓(カナ)</InputLabel>
            <InputField<UserRequestInterface>
              controller={{
                control,
                name: 'family_name_kana',
              }}
              inputProps={{
                placeholder: '入力してください',
              }}
            />
          </Col>
          <Col xs={11} className="ml-10">
            <Row className="justify-content-between">
              <InputLabel className="font-weight-bold">名(カナ)</InputLabel>
              <LabelRequire>
                <span>必須</span>
              </LabelRequire>
            </Row>
            <InputField<UserRequestInterface>
              controller={{
                control,
                name: 'given_name_kana',
              }}
              inputProps={{
                placeholder: '入力してください',
              }}
            />
          </Col>
        </Row>

        <Col xs={24} className="mt-20">
          <Row className="justify-content-between">
            <InputLabel className="font-weight-bold">メールアドレス</InputLabel>
            <LabelRequire>
              <span>必須</span>
            </LabelRequire>
          </Row>
          <InputField<UserRequestInterface>
            controller={{
              control,
              name: 'email',
            }}
            inputProps={{
              placeholder: '入力してください',
              type: 'email',
              onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
                handleBlur(e.target.value),
            }}
          />
        </Col>

        {isRequireGender && (
          <Row className="mt-20">
            <Col xs={24}>
              <Row className="justify-content-between">
                <InputLabel className="font-weight-bold">
                  性別（どちらかを選択してください）
                </InputLabel>
                <LabelRequire>
                  <span>必須</span>
                </LabelRequire>
              </Row>
              <GenderSelection
                value={userSelectedGender}
                onChange={(val) => {
                  setUserSelectedGender(val)
                  setGenderError(false)
                  newForm.setValue('gender_id', val)
                  newForm.clearErrors('gender_id')
                }}
              />
              {genderError && (
                <Typography.Text
                  className="ant-form-item-explain ant-form-item-explain-error pt-1"
                  type="danger"
                  style={{
                    overflow: 'visible',
                    whiteSpace: 'break-spaces',
                    display: 'block',
                  }}
                >
                  選択してください。
                </Typography.Text>
              )}

              {newForm.formState.errors.gender_id && (
                <Typography.Text
                  className="ant-form-item-explain ant-form-item-explain-error pt-1"
                  type="danger"
                >
                  {newForm.formState.errors.gender_id.message}
                </Typography.Text>
              )}
            </Col>
          </Row>
        )}

        <Col xs={24} className="mt-15 d-flex">
          <label className="agree-policy-label">
            <input
              type="checkbox"
              checked={isAgreePolicy}
              onChange={(e) => setIsAgreePolicy(e.target.checked)}
            />
            <span>
              <a
                href="https://bioportplus.jp/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                利用規約
              </a>
              および、
              <a
                href="https://bioportplus.jp/policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                プライバシーポリシー
              </a>
              に同意する
            </span>
          </label>
        </Col>

        <ButtonCustom
          className="btn-default btn-chestnut-rose br-60 mt-30"
          loading={isSubmitting}
          htmlType="button"
          onClick={handleSubmit(handleFormSubmit, handleFormError)}
          name="次へ進む"
          showIconRight
          disabled={!isAgreePolicy}
        />
      </StyleForm>

      <DuplicateAccountModal
        open={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
      />
    </React.Fragment>
  )
}

const StyleForm = styled(Form)`
  .agree-policy-label {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--color-black, #333);
    font-size: 14px;

    a {
      color: #1890ff;
      text-decoration: underline;
    }
  }

  .input-birthday {
    .aga-input {
      font-size: 16px;
    }
  }

  .picker-group {
    position: fixed;
    width: 100%;
    bottom: 0;
    left: 0;
    height: 100vh;
    background: #000000a8;
    z-index: 100;

    &__item {
      position: absolute;
      width: 100%;
      background-color: #fff;
      bottom: 0;
      left: 0;
      font-size: 16px;
      border-radius: 10px 10px 0 0;

      .birth-action {
        background-color: #ffffff;
        padding: 15px;
        border-bottom: 1px solid #998800;
        border-radius: 10px 10px 0 0;
        justify-content: space-between;

        .btn-warning {
          color: var(--color-dusty-gray);
          background-color: var(--color-white);
          cursor: pointer;
        }

        .btn-gray {
          color: #999999;
          cursor: pointer;
        }
      }
    }
  }

  .picker-date {
    background-color: #f1efe7;
    border: 1px solid #cfc392;
    padding: 10px;
  }

  .picker-inner {
    -webkit-mask-box-image-source: none !important;
  }

  .picker-container .picker-column .picker-item.picker-item-selected {
    font-weight: bold;
  }
`

export default UserInfoForm
